import { useCallback } from "react"
import type { FieldError } from "react-hook-form"

import type { UseToastOptions } from "@chakra-ui/react"
import { useToast } from "@chakra-ui/react"

import type { FetchResult } from "@apollo/client/link/core"
import * as Sentry from "@sentry/nextjs"

export interface ValidationError {
  property: string
  constraints: { [key: string]: string }
}

export function formatValidations(errors: ValidationError[]): FieldError[] {
  return errors.map((error) => ({
    name: error.property,
    type: error.property,
    types: error.constraints,
  }))
}

export interface MutationHandler<T> {
  onSuccess?: (data: T) => Promise<any> | any
  onValidationError?: (errors: FieldError[]) => void
  onAppError?: (message: string) => void
  onServerError?: (message: string) => void
  onFinish?: () => void
}

function mutationHandler<T>(
  res: FetchResult<T>,
  handler: MutationHandler<T>,
  toast: (props: UseToastOptions) => void,
  actions?: {
    setFieldErrors: (errors: FieldError[]) => void
    setAppError: (message: any) => void
  },
) {
  try {
    if (!res) throw new Error("No response")

    if (res.data && !res.errors && handler?.onSuccess) {
      handler.onSuccess(res.data)
      return
    }

    const error = res.errors?.[0]

    if (!error) {
      return
    }

    if (
      error.message.includes("Access denied!") ||
      error.message.includes("Not authorized")
    ) {
      toast({
        status: "error",
        description: "You cannot perform this action",
      })
      return
    }

    if (error.extensions?.code === "FORBIDDEN") {
      if (handler.onServerError) {
        handler.onServerError(error.message)
      } else {
        toast({ status: "error", description: error.message })
      }
      handler?.onAppError?.(error.message)
      return
    }

    if (error.message.includes("Unique constraint failed")) {
      toast({
        status: "error",
        description: "Value must be unique",
      })
      return
    }

    if (error.message.includes("ERROR_IN_REFUND_AND_DELETE_SHIPMENT")) {
      toast({
        status: "error",
        description: `${error.message.replace(
          "ERROR_IN_REFUND_AND_DELETE_SHIPMENT",
          "",
        )}`,
      })
      return
    }

    if (error.extensions?.exception?.validationErrors) {
      const validationErrors = error.extensions?.exception?.validationErrors
      if (handler?.onValidationError) {
        handler.onValidationError(formatValidations(validationErrors))
      } else if (actions) {
        actions.setFieldErrors(formatValidations(validationErrors))
      }
      return
    }

    if (error.extensions?.code === "BAD_USER_INPUT") {
      toast({ status: "error", description: error.message })
      handler?.onAppError?.(error.message)
      return
    }

    // If there is an error handler, let it handle the error.
    if (error && handler.onServerError) {
      handler.onServerError(error.message)
      return
    }

    toast({
      status: "error",
      description: "An unexpected server error occurred",
    })
  } catch (e) {
    Sentry.captureException(e)
    console.log(e)
    toast({
      status: "error",
      description: "Server error. We have been notified.",
    })
  } finally {
    if (handler?.onFinish) {
      handler.onFinish()
    }
  }
  return res
}

export function useMutationHandler() {
  const toast = useToast()
  const handle = useCallback(
    async <T>(
      mutation: () => Promise<FetchResult<T>>,
      actions?: MutationHandler<T>,
      formActions?: {
        setFieldErrors: (errors: FieldError[]) => void
        setAppError: (message: any) => void
      },
    ) => {
      try {
        const res = await mutation()
        return mutationHandler(res, actions || {}, toast, formActions)
      } catch (e: any) {
        Sentry.captureException(e)
        console.log(e)
        toast({
          description:
            "Something went wrong. Please refresh page and try again.",
          status: "error",
        })
        if (actions?.onServerError) actions.onServerError(e?.message || "Error")
        return
      }
    },
    [toast],
  )
  return handle
}
